import { quitarMarcasDeAcronimos } from '../lib/acronimos';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Languages, Building2, Highlighter, Link2, Scissors, Smile, Sparkles, Users, Wrench, X } from 'lucide-react';
import { pegarLimpio } from '../lib/pasteHtml';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { getDraft, setDraft, clearDraft } from '../lib/draftStore';
import { DictateButton } from './ai/DictateButton';

type Target = { id: string; name: string; kind: 'user' | 'team' | 'department' | 'contractor'; alias?: string };
type Gif = { id: string; url: string; preview: string | null };

// Emojis frecuentes para el picker del composer (sin dependencias externas).
const EMOJIS = ['👍', '🙏', '🔥', '✅', '❌', '⚠️', '🎉', '💪', '👏', '🙌', '😀', '😅', '😂', '🤣', '😊', '😍', '😎', '🤔', '😐', '😢', '😭', '😡', '🥳', '🤝', '👀', '💯', '⭐', '❤️', '💰', '📎', '📅', '📞', '📧', '🏠', '🔧', '🏢', '📝', '⏰', '🚀', '✍️'];

export type RichEditorHandle = {
  getHtml: () => string;
  getText: () => string;
  clear: () => void;
  focus: () => void;
  insertHtml: (html: string) => void;
};

/**
 * Editor de texto RICO estilo Monday: negritas / cursiva / tachado / resaltado / bullets
 * + GIFs (GIPHY vía el proxy del server) + @menciones (usuarios/equipos/deptos).
 * No controlado: el padre lee el contenido con la ref (getHtml/getText) y limpia con clear().
 * onChange avisa si hay contenido (para habilitar el botón de enviar).
 */
export const RichEditor = forwardRef<RichEditorHandle, {
  placeholder?: string; minHeight?: number; onEnter?: () => void; mentions?: boolean;
  onChange?: (hasContent: boolean) => void; initialHtml?: string;
  onPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  /** Si se pasa, el borrador se guarda por esta clave y SOBREVIVE al cambio de tab (sessionStorage). */
  draftKey?: string;
  /** Nombre del contratista del claim → habilita la mención @contractor (inserta la compañía). */
  contractorName?: string | null;
}>(function RichEditor({ placeholder, minHeight = 64, onEnter, mentions = false, onChange, initialHtml, onPaste, draftKey, contractorName }, ref) {
  const { t } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [mOpen, setMOpen] = useState(false);
  const [mQuery, setMQuery] = useState('');
  const [mSel, setMSel] = useState(0);
  const [gifOpen, setGifOpen] = useState(false);
  const [gifQ, setGifQ] = useState('');
  const [gifs, setGifs] = useState<Gif[]>([]);
  const [gifBusy, setGifBusy] = useState(false);
  const [gifsOff, setGifsOff] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [dictErr, setDictErr] = useState<string | null>(null);
  /**
   * DICTADO (pedido del dueño 2026-10-07: "ponle el micrófono al editor"): el audio va a Whisper y el
   * modelo Dictation de Settings → AI lo ordena; lo que vuelve se inserta en el caret como texto plano
   * (saltos de línea → <br>), en un renglón nuevo si ya había algo escrito. Vale para notas del claim,
   * chat, correos y todo lo que use este editor.
   */
  function insertarDictado(text: string) {
    const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
    const habia = Boolean(box.current?.textContent?.trim());
    box.current?.focus();
    document.execCommand('insertHTML', false, `${habia ? '<br>' : ''}${safe}`);
    notify();
  }
  function avisarDictado(msg: string) { setDictErr(msg); window.setTimeout(() => setDictErr(null), 5000); }
  const [empty, setEmpty] = useState(true);

  // Asistente de escritura IA (como Monday): mejora, acorta o TRADUCE AL INGLÉS lo escrito.
  async function aiRewrite(mode: 'improve' | 'shorten' | 'translate_en') {
    const text = (box.current?.innerText ?? '').trim();
    if (!text) { setAiOpen(false); return; }
    setAiBusy(true); setAiOpen(false);
    try {
      const r = await api.post<{ text: string }>('/api/ai/rewrite', { text, mode });
      if (box.current && r.text) {
        const safe = r.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
        box.current.innerHTML = safe;
        notify();
      }
    } catch { /* si falla la IA, deja el texto como está */ }
    finally { setAiBusy(false); }
  }

  useEffect(() => {
    if (!mentions) return;
    api.get<{ users: Array<{ id: string; name: string }>; teams: Array<{ id: string; name: string }>; departments?: Array<{ id: string; name: string }>; groups?: Array<{ id: string; name: string }> }>('/api/team/mention-targets')
      .then((r) => setTargets([
        // Grupos derivados (@Management = managers sin depto): se pintan como un team más.
        ...(r.groups || []).map((g) => ({ ...g, kind: 'team' as const })),
        ...(r.departments || []).map((d) => ({ ...d, kind: 'department' as const })),
        ...(r.teams || []).map((x) => ({ ...x, kind: 'team' as const })),
        ...(r.users || []).map((u) => ({ ...u, kind: 'user' as const })),
      ])).catch(() => {});
  }, [mentions]);

  // Al montar: siembra el borrador guardado (sobrevive cambio de tab/vista) O initialHtml (editar
  // una nota). El BORRADOR gana si existe: si Paula estaba editando y se fue a Files, al volver
  // quiere SU texto a medio escribir, no el original (pedido 2026-08-14). Sin el notify() el
  // placeholder queda encima y Send deshabilitado.
  useEffect(() => {
    if (!box.current) return;
    const seed = (draftKey ? getDraft(draftKey) : '') || initialHtml || '';
    if (seed) { box.current.innerHTML = seed; notify(); }
    /* eslint-disable-next-line */
  }, []);

  // Buscador de GIFs con debounce; al abrir sin query trae trending.
  useEffect(() => {
    if (!gifOpen) return;
    setGifBusy(true);
    const h = setTimeout(() => {
      api.get<{ gifs: Gif[]; disabled?: boolean }>(`/api/gifs/search${gifQ.trim() ? `?q=${encodeURIComponent(gifQ.trim())}` : ''}`)
        .then((r) => { setGifs(r.gifs); setGifsOff(!!r.disabled); })
        .catch(() => setGifs([]))
        .finally(() => setGifBusy(false));
    }, gifQ ? 350 : 0);
    return () => clearTimeout(h);
  }, [gifOpen, gifQ]);

  const notify = () => {
    const el = box.current;
    const has = !!el && (el.innerText.trim().length > 0 || el.querySelector('img') != null);
    setEmpty(!has);
    // Persiste el borrador para que sobreviva el cambio de tab (se limpia solo si queda vacío).
    if (draftKey) setDraft(draftKey, el?.innerHTML ?? '');
    onChange?.(has);
  };

  useImperativeHandle(ref, () => ({
    // Lo que sale del editor nunca lleva la marca de los acrónimos (es un adorno de pantalla; un
    // borrador viejo en localStorage puede traerla).
    getHtml: () => quitarMarcasDeAcronimos(box.current?.innerHTML ?? ''),
    getText: () => box.current?.innerText ?? '',
    clear: () => { if (box.current) box.current.innerHTML = ''; if (draftKey) clearDraft(draftKey); setEmpty(true); onChange?.(false); },
    focus: () => box.current?.focus(),
    insertHtml: (html: string) => { box.current?.focus(); document.execCommand('insertHTML', false, html); notify(); },
  }));

  const exec = (cmd: string, val?: string) => {
    box.current?.focus();
    document.execCommand(cmd, false, val);
    notify();
  };

  function insertGif(g: Gif) {
    box.current?.focus();
    document.execCommand('insertHTML', false, `<img src="${g.url}" alt="gif" style="max-height:160px;border-radius:8px;display:block;margin:4px 0">`);
    setGifOpen(false); setGifQ('');
    notify();
  }

  // ---- @menciones en contentEditable: token "@..." justo antes del caret ----
  function textBeforeCaret(): string | null {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || sel.anchorNode.nodeType !== Node.TEXT_NODE) return null;
    if (!box.current?.contains(sel.anchorNode)) return null;
    return (sel.anchorNode.textContent ?? '').slice(0, sel.anchorOffset);
  }
  function refreshMentions() {
    if (!mentions) return;
    const upto = textBeforeCaret();
    const m = upto != null ? /@([^@\n]{0,40})$/.exec(upto) : null;
    if (m) { setMQuery(m[1]!); setMSel(0); setMOpen(true); } else setMOpen(false);
  }
  // @contractor: mención especial que inserta el NOMBRE de la compañía del contratista (para que
  // en el DM se lea claro quién es). Matchea escribiendo "contractor" o el nombre de la compañía.
  const contractorTarget: Target | null = mentions && contractorName?.trim()
    ? { id: 'contractor', name: contractorName.trim(), kind: 'contractor', alias: 'contractor' }
    : null;
  const allTargets = contractorTarget ? [contractorTarget, ...targets] : targets;
  const suggestions = mOpen
    ? allTargets.filter((x) => { const q = mQuery.toLowerCase(); return x.name.toLowerCase().includes(q) || (x.alias?.includes(q) ?? false); }).slice(0, 8)
    : [];
  function pickMention(name: string) {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode || sel.anchorNode.nodeType !== Node.TEXT_NODE) { setMOpen(false); return; }
    const node = sel.anchorNode as Text;
    const upto = (node.textContent ?? '').slice(0, sel.anchorOffset);
    const m = /@([^@\n]{0,40})$/.exec(upto);
    if (!m) { setMOpen(false); return; }
    const start = upto.length - m[0].length;
    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, sel.anchorOffset);
    sel.removeAllRanges(); sel.addRange(range);
    // Mención como CHIP con el acento del app (.msg-mention — antes era azul fijo #2563eb y no
    // matcheaba el morado del resto, pedido de David). El backend la sigue detectando por el
    // texto '@Nombre' dentro del span. El &nbsp; va FUERA del span → lo que se escriba después
    // NO hereda el estilo.
    const safe = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    document.execCommand('insertHTML', false, `<span class="msg-mention">@${safe}</span>&nbsp;`);
    setMOpen(false);
    notify();
  }

  const inList = () => { try { return document.queryCommandState('insertUnorderedList'); } catch { return false; } };

  const tb: React.CSSProperties = { border: 0, background: 'transparent', cursor: 'pointer', borderRadius: 6, padding: '3px 7px', fontSize: 'var(--fs-xs)', lineHeight: 1.3, color: 'var(--ink)' };

  return (
    <div style={{ position: 'relative' }}>
      {/* Toolbar de formato (estilo Monday) */}
      <div style={{ display: 'flex', gap: 2, alignItems: 'center', border: '1px solid var(--line)', borderBottom: 0, borderRadius: '8px 8px 0 0', padding: '3px 6px', background: 'var(--surface-2)', flexWrap: 'wrap' }}>
        <button type="button" tabIndex={-1} style={{ ...tb, fontWeight: 800 }} title={t('re.bold')} onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}>B</button>
        <button type="button" tabIndex={-1} style={{ ...tb, fontStyle: 'italic' }} title={t('re.italic')} onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}>I</button>
        <button type="button" tabIndex={-1} style={{ ...tb, textDecoration: 'underline' }} title={t('re.underline')} onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}>U</button>
        <button type="button" tabIndex={-1} style={{ ...tb, textDecoration: 'line-through' }} title={t('re.strike')} onMouseDown={(e) => { e.preventDefault(); exec('strikeThrough'); }}>S</button>
        <button type="button" tabIndex={-1} style={tb} title={t('re.highlight')} onMouseDown={(e) => { e.preventDefault(); exec('hiliteColor', '#fef08a'); }}><Highlighter size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
        {/* Tamaño del texto (pedido 2026-08-14): aplica al texto seleccionado o a lo que se escriba después. */}
        <button type="button" tabIndex={-1} style={{ ...tb, fontWeight: 700 }} title={t('re.textSize')} onMouseDown={(e) => { e.preventDefault(); setSizeOpen((o) => !o); setEmojiOpen(false); setGifOpen(false); setAiOpen(false); }}>Aa</button>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)', margin: '2px 4px' }} />
        <button type="button" tabIndex={-1} style={tb} title={t('re.bullets')} onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>• ≡</button>
        <button type="button" tabIndex={-1} style={tb} title={t('re.numbered')} onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>1. ≡</button>
        <button type="button" tabIndex={-1} style={tb} title={t('re.link')} onMouseDown={(e) => { e.preventDefault(); const url = window.prompt(t('re.linkPrompt')); if (url && url.trim()) exec('createLink', url.trim()); }}><Link2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)', margin: '2px 4px' }} />
        <button type="button" tabIndex={-1} style={tb} title={t('re.emoji')} onMouseDown={(e) => { e.preventDefault(); setEmojiOpen((o) => !o); setGifOpen(false); setAiOpen(false); }}><Smile size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
        <button type="button" tabIndex={-1} style={{ ...tb, fontWeight: 700 }} title="GIF" onMouseDown={(e) => { e.preventDefault(); setGifOpen((o) => !o); setEmojiOpen(false); setAiOpen(false); }}>GIF</button>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--line)', margin: '2px 4px' }} />
        <button type="button" tabIndex={-1} disabled={aiBusy} style={{ ...tb, opacity: aiBusy ? 0.5 : 1 }} title={t('re.ai')} onMouseDown={(e) => { e.preventDefault(); setAiOpen((o) => !o); setEmojiOpen(false); setGifOpen(false); }}>{aiBusy ? '…' : <Sparkles size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />}</button>
        <DictateButton compact endpoint="/api/ai/transcribe" onText={insertarDictado} onError={avisarDictado} />
      </div>
      {dictErr && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)', padding: '2px 8px' }}>{dictErr}</div>}
      {/* Menú de TAMAÑO del texto: execCommand('fontSize', 1-7) — viaja bien en emails (<font size>). */}
      {sizeOpen && (
        <div style={{ position: 'absolute', zIndex: 31, left: 6, top: 34, minWidth: 150, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.18)', overflow: 'hidden' }}>
          {([['2', 're.sizeSmall', 12], ['3', 're.sizeNormal', 14], ['5', 're.sizeLarge', 18], ['6', 're.sizeHuge', 22]] as const).map(([val, k, px]) => (
            <button key={val} type="button" onMouseDown={(e) => { e.preventDefault(); setSizeOpen(false); exec('fontSize', val); }}
              style={{ display: 'block', width: '100%', padding: '7px 12px', border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: px, color: 'var(--ink)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>{t(k)}</button>
          ))}
        </div>
      )}
      {/* Menú del asistente IA: mejorar / acortar / traducir al inglés (como el ✍️ de Monday). */}
      {aiOpen && (
        <div style={{ position: 'absolute', zIndex: 31, right: 6, top: 34, minWidth: 170, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.18)', overflow: 'hidden' }}>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); aiRewrite('improve'); }} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '9px 12px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: 'var(--fs-sm)', textAlign: 'left' }} onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}><Sparkles size={14} strokeWidth={1.75} /> {t('re.aiImprove')}</button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); aiRewrite('shorten'); }} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '9px 12px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: 'var(--fs-sm)', textAlign: 'left' }} onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}><Scissors size={14} strokeWidth={1.75} /> {t('re.aiShorten')}</button>
          {/* Traducir al inglés (pedido 2026-08-24): media empresa escribe en español y del otro
              lado leen en inglés. Va acá y no en un botón aparte para no ensuchar la barra. */}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); aiRewrite('translate_en'); }} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, padding: '9px 12px', border: 0, background: 'transparent', cursor: 'pointer', fontSize: 'var(--fs-sm)', textAlign: 'left' }} onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}><Languages size={14} strokeWidth={1.75} /> {t('re.aiTranslate')}</button>
        </div>
      )}
      {/* Picker de EMOJIS (como Monday/Box): inserta el emoji en el caret. */}
      {emojiOpen && (
        <div style={{ position: 'absolute', zIndex: 31, left: 6, top: 34, width: 'min(300px, calc(100vw - 48px))', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.18)', padding: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 2, maxHeight: 200, overflowY: 'auto' }}>
            {EMOJIS.map((em) => (
              <button key={em} type="button" onMouseDown={(e) => { e.preventDefault(); box.current?.focus(); document.execCommand('insertText', false, em); setEmojiOpen(false); notify(); }}
                style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: 'var(--fs-lg)', lineHeight: 1, padding: 4, borderRadius: 6 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>{em}</button>
            ))}
          </div>
        </div>
      )}
      {/* Área editable — envuelta en su PROPIO relative para que el placeholder se ancle a ella
          (antes estaba clavado a top:34 del wrapper y se ENCIMABA con el toolbar cuando éste se
          partía en dos líneas en contenedores angostos, ej. el General chat del drawer). */}
      <div style={{ position: 'relative' }}>
      <div ref={box} contentEditable suppressContentEditableWarning
        className="input"
        data-placeholder={placeholder || ''}
        style={{ minHeight, overflowY: 'auto', fontSize: 'var(--fs-sm)', lineHeight: 1.45, borderRadius: '0 0 8px 8px', whiteSpace: 'pre-wrap', resize: 'vertical', maxHeight: 500 }}
        onInput={() => { refreshMentions(); notify(); }}
        onKeyUp={refreshMentions}
        onClick={refreshMentions}
        onPaste={(e) => {
          // Word/Outlook/Docs pegan un documento entero (mso-*, <o:p>, listas falsas, estilos en
          // cada nodo) y el navegador lo mete tal cual: viñetas rotas, saltos de más y texto
          // descolocado — que además viaja así en el email. Se limpia ANTES de insertar. Ver
          // lib/pasteHtml.ts. Si el padre trae su propio onPaste, corre después.
          if (!e.defaultPrevented) pegarLimpio(e);
          if (!e.defaultPrevented) onPaste?.(e);
          else { onPaste?.(e); notify(); }
        }}
        onBlur={() => setTimeout(() => setMOpen(false), 150)}
        onKeyDown={(e) => {
          if (mOpen && suggestions.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setMSel((s) => (s + 1) % suggestions.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setMSel((s) => (s - 1 + suggestions.length) % suggestions.length); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(suggestions[mSel]!.name); return; }
            if (e.key === 'Escape') { setMOpen(false); return; }
          }
          // Enter envía (como antes); Shift+Enter = salto; dentro de una lista, Enter hace otro bullet.
          // `e.repeat` = la tecla se quedó apretada y el navegador la repite ~11 veces por
          // segundo. Sin esto, dejar el Enter apretado publicaba una nota por repetición.
          if (onEnter && e.key === 'Enter' && !e.shiftKey && !e.repeat && !inList()) { e.preventDefault(); onEnter(); }
          else if (e.key === 'Enter' && !e.shiftKey && e.repeat && !inList()) e.preventDefault();
        }} />
      {/* Placeholder simulado (contentEditable no tiene) — anclado al área editable. */}
      {empty && placeholder && (
        <div style={{ position: 'absolute', top: 9, left: 12, right: 8, fontSize: 'var(--fs-sm)', color: 'var(--muted)', pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{placeholder}</div>
      )}
      </div>
      {/* Dropdown de @menciones */}
      {mOpen && suggestions.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 30, left: 6, top: '100%', minWidth: 200, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,.12)', overflow: 'hidden' }}>
          {suggestions.map((m, i) => (
            <div key={`${m.kind}-${m.id}`} onMouseDown={(e) => { e.preventDefault(); pickMention(m.name); }}
              style={{ padding: '6px 10px', fontSize: 'var(--fs-sm)', cursor: 'pointer', background: i === mSel ? 'var(--surface-2)' : 'transparent', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-flex' }}>{m.kind === 'team' ? <Users size={13} strokeWidth={1.75} /> : m.kind === 'department' ? <Building2 size={13} strokeWidth={1.75} /> : m.kind === 'contractor' ? <Wrench size={13} strokeWidth={1.75} /> : '@'}</span>{m.name}
              {m.kind === 'contractor' && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>· contractor</span>}
            </div>
          ))}
        </div>
      )}
      {/* Picker de GIFs */}
      {gifOpen && (
        <div style={{ position: 'absolute', zIndex: 30, right: 0, top: 30, width: 'min(320px, calc(100vw - 48px))', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.18)', padding: 8, display: 'grid', gap: 6 }}>
          <input className="input sm" autoFocus placeholder={t('re.gifSearch')} value={gifQ} onChange={(e) => setGifQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setGifOpen(false); }} />
          {gifsOff ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 8 }}>{t('re.gifOff')}</div>
            : gifBusy ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 8 }}>…</div>
            : gifs.length === 0 ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 8 }}>—</div>
            : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, maxHeight: 260, overflowY: 'auto' }}>
                {gifs.map((g) => (
                  <button key={g.id} type="button" onMouseDown={(e) => { e.preventDefault(); insertGif(g); }}
                    style={{ border: 0, padding: 0, cursor: 'pointer', background: 'transparent' }}>
                    <img src={g.preview || g.url} alt="gif" style={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 6 }} />
                  </button>
                ))}
              </div>
            )}
          <button className="btn ghost sm" onClick={() => setGifOpen(false)} style={{ justifySelf: 'end' }}><X size={13} strokeWidth={1.75} /></button>
        </div>
      )}
    </div>
  );
});
